/**
 * Markdown Editor Component
 * 
 * Simple markdown editor with live preview.
 * Uses a textarea for editing and renders markdown preview.
 */

import React, { useState } from 'react';
import {
  Box,
  TextField,
  Paper,
  Tabs,
  Tab,
  Typography,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownEditorProps {
  /** Markdown content */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Minimum height */
  minHeight?: number;
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  placeholder = 'Write your blog post content in Markdown...',
  minHeight = 400,
}) => {
  const [tab, setTab] = useState<'edit' | 'preview' | 'split'>('split');

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(_, newValue) => setTab(newValue)}
        sx={{ mb: 2 }}
      >
        <Tab label="Edit" value="edit" />
        <Tab label="Preview" value="preview" />
        <Tab label="Split" value="split" />
      </Tabs>

      <Box
        sx={{
          display: 'flex',
          gap: 2,
          minHeight: `${minHeight}px`,
        }}
      >
        {/* Editor */}
        {(tab === 'edit' || tab === 'split') && (
          <Box
            sx={{
              flex: tab === 'split' ? 1 : 'none',
              width: tab === 'split' ? '50%' : '100%',
            }}
          >
            <TextField
              fullWidth
              multiline
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
              variant="outlined"
              sx={{
                '& .MuiInputBase-root': {
                  minHeight: `${minHeight}px`,
                  fontFamily: 'monospace',
                  fontSize: '14px',
                },
              }}
            />
          </Box>
        )}

        {/* Preview */}
        {(tab === 'preview' || tab === 'split') && (
          <Box
            sx={{
              flex: tab === 'split' ? 1 : 'none',
              width: tab === 'split' ? '50%' : '100%',
            }}
          >
            <Paper
              sx={{
                p: 2,
                minHeight: `${minHeight}px`,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'auto',
              }}
            >
              {value ? (
                <Box
                  sx={{
                    '& h1': { fontSize: '2em', fontWeight: 'bold', mb: 1 },
                    '& h2': { fontSize: '1.5em', fontWeight: 'bold', mb: 1 },
                    '& h3': { fontSize: '1.25em', fontWeight: 'bold', mb: 1 },
                    '& p': { mb: 1 },
                    '& ul, & ol': { mb: 1, pl: 2 },
                    '& code': {
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      padding: '2px 4px',
                      borderRadius: '2px',
                      fontFamily: 'monospace',
                    },
                    '& pre': {
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                      padding: '8px',
                      borderRadius: '4px',
                      overflow: 'auto',
                    },
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {value}
                  </ReactMarkdown>
                </Box>
              ) : (
                <Typography color="text.secondary" variant="body2">
                  Preview will appear here...
                </Typography>
              )}
            </Paper>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MarkdownEditor;

